"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropContentPresentation } from "../dropContentPresentation";
import type { DropIdentityMode, DropInteractionParams, DropLocation, DropTimestampLayout } from "../drop.types";
import { hasDropFooter } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(108);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    footer,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t4,
    isVotingControlsLocked: t5,
    contentPresentation: t6,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const isVotingClosed = t4 === undefined ? false : t4;
  const isVotingControlsLocked = t5 === undefined ? false : t5;
  const contentPresentation = t6 === undefined ? "default" : t6;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let identityProfile;
  let t7;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t8;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t8 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t8;
    } else {
      t8 = $[8];
    }
    visibleMetadata = t8;
    t7 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t7;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t7 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t7;
  const showIdentity = identityMode !== "hidden";
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const {
    isOpen: isVoteModalOpen,
    open: openVoteModal,
    close: closeVoteModal
  } = useVotingModalState(isVotingActionLocked);
  let t8;
  if ($[9] !== hasTouch || $[10] !== showInteractions) {
    t8 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[9] = hasTouch;
    $[10] = showInteractions;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleLongPress = t8;
  let t9;
  if ($[12] !== activePartIndex || $[13] !== drop || $[14] !== onReply) {
    t9 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[12] = activePartIndex;
    $[13] = drop;
    $[14] = onReply;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleOnReply = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsSlideUp(false);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[17] !== openVoteModal) {
    t11 = () => {
      openVoteModal();
    };
    $[17] = openVoteModal;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleVoteButtonClick = t11;
  let t12;
  if ($[19] !== canShowVote || $[20] !== drop || $[21] !== handleVoteButtonClick || $[22] !== isVotingActionLocked || $[23] !== showInteractions) {
    t12 = canShowVote && showInteractions && !isVotingActionLocked ? <VotingModalButton drop={drop} onClick={handleVoteButtonClick} /> : null;
    $[19] = canShowVote;
    $[20] = drop;
    $[21] = handleVoteButtonClick;
    $[22] = isVotingActionLocked;
    $[23] = showInteractions;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const voteAction = t12;
  const t13 = !(typeof winningThreshold === "number" && winningThreshold > 0);
  let t14;
  if ($[25] !== activePartIndex || $[26] !== drop || $[27] !== handleOnReply || $[28] !== isMobile || $[29] !== showInteractions || $[30] !== showReplyAndQuote) {
    t14 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[25] = activePartIndex;
    $[26] = drop;
    $[27] = handleOnReply;
    $[28] = isMobile;
    $[29] = showInteractions;
    $[30] = showReplyAndQuote;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== drop || $[33] !== showIdentity) {
    t15 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[32] = drop;
    $[33] = showIdentity;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== drop || $[36] !== identityMode || $[37] !== showIdentity || $[38] !== showWaveInfo || $[39] !== timestampLayout || $[40] !== winningThreshold) {
    t16 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} winningThreshold={winningThreshold} timestampLayout={timestampLayout} />);
    $[35] = drop;
    $[36] = identityMode;
    $[37] = showIdentity;
    $[38] = showWaveInfo;
    $[39] = timestampLayout;
    $[40] = winningThreshold;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== activePartIndex || $[43] !== contentPresentation || $[44] !== drop || $[45] !== embedDepth || $[46] !== embedPath || $[47] !== handleLongPress || $[48] !== maxEmbedDepth || $[49] !== onDropContentClick || $[50] !== onQuoteClick || $[51] !== quotePath) {
    t17 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[42] = activePartIndex;
    $[43] = contentPresentation;
    $[44] = drop;
    $[45] = embedDepth;
    $[46] = embedPath;
    $[47] = handleLongPress;
    $[48] = maxEmbedDepth;
    $[49] = onDropContentClick;
    $[50] = onQuoteClick;
    $[51] = quotePath;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t16 || $[54] !== t17) {
    t18 = <div className="tw-flex tw-w-full tw-flex-col">{t16}{t17}</div>;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t15 || $[57] !== t18) {
    t19 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t15}{t18}</div>;
    $[56] = t15;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== drop.id || $[60] !== identityProfile || $[61] !== isSelfNominee || $[62] !== showIdentity) {
    t20 = identityProfile && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-px-4`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[59] = drop.id;
    $[60] = identityProfile;
    $[61] = isSelfNominee;
    $[62] = showIdentity;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== drop.id || $[65] !== visibleMetadata) {
    t21 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[64] = drop.id;
    $[65] = visibleMetadata;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== drop || $[68] !== footer || $[69] !== isVotingClosed || $[70] !== isVotingControlsLocked || $[71] !== showInteractions || $[72] !== voteAction || $[73] !== winningThreshold || $[74] !== winningThresholdMinDurationMs) {
    t22 = (showInteractions || !hasDropFooter(footer)) && <ParticipationDropFooter drop={drop} voteAction={voteAction} showInteractions={showInteractions} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[67] = drop;
    $[68] = footer;
    $[69] = isVotingClosed;
    $[70] = isVotingControlsLocked;
    $[71] = showInteractions;
    $[72] = voteAction;
    $[73] = winningThreshold;
    $[74] = winningThresholdMinDurationMs;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== footer || $[77] !== showIdentity) {
    t23 = hasDropFooter(footer) && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-px-4 tw-pb-4 tw-pt-2`}>{footer}</div>;
    $[76] = footer;
    $[77] = showIdentity;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== t20 || $[80] !== t21 || $[81] !== t22 || $[82] !== t23) {
    t24 = <div className="tw-flex tw-w-full tw-flex-col">{t20}{t21}{t22}{t23}</div>;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
    $[82] = t23;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  let t25;
  if ($[84] !== closeVoteModal || $[85] !== drop || $[86] !== isMobileScreen || $[87] !== isVoteModalOpen || $[88] !== showInteractions) {
    t25 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} /> : <VotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} />);
    $[84] = closeVoteModal;
    $[85] = drop;
    $[86] = isMobileScreen;
    $[87] = isVoteModalOpen;
    $[88] = showInteractions;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== drop || $[91] !== handleOnReply || $[92] !== isSlideUp || $[93] !== isVotingActionLocked || $[94] !== longPressTriggered || $[95] !== showInteractions || $[96] !== showReplyAndQuote) {
    t26 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} showVoting={!isVotingActionLocked} />;
    $[90] = drop;
    $[91] = handleOnReply;
    $[92] = isSlideUp;
    $[93] = isVotingActionLocked;
    $[94] = longPressTriggered;
    $[95] = showInteractions;
    $[96] = showReplyAndQuote;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  let t27;
  if ($[98] !== drop || $[99] !== isActiveDrop || $[100] !== location || $[101] !== t13 || $[102] !== t14 || $[103] !== t19 || $[104] !== t24 || $[105] !== t25 || $[106] !== t26) {
    t27 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location} useRankStyles={t13}>{t14}{t19}{t24}{t25}{t26}</ParticipationDropContainer>;
    $[98] = drop;
    $[99] = isActiveDrop;
    $[100] = location;
    $[101] = t13;
    $[102] = t14;
    $[103] = t19;
    $[104] = t24;
    $[105] = t25;
    $[106] = t26;
    $[107] = t27;
  } else {
    t27 = $[107];
  }
  return t27;
}